/**
 * Blox-Design plan page revamp tokens (file rZ4XNmpD7wDFKzIfrkNmhr)
 *
 * Figma frame mapping:
 * - 75644:13794 — Plan toolbar (View Inputs, filter pill, view tabs, What Ifs)
 * - 75644:14192 — Title bar + KPI summary row
 * - 75644:11037 — KPI summary bar (active + inactive metric cards)
 * - 75644:14203 — Builder mode pill
 * - 75644:11968 — Chart section + toolbar
 * - 75644:7652  — Chart card (toolbar, plot, legend)
 * - 75644:12599 — Data table section
 * - 75644:8226  — Data table card (toolbar + grid)
 * - 75644:10724 — Toolbar filter/group pill icons
 * - 75644:10729 — Filter / group-by pill (filter funnel, grip, chevron)
 * - 75911:3707  — Inputs sidebar
 * - 75644:10993 — Inputs left panel (driver list)
 * - 75916:7128  — What-ifs panel open (legacy frame)
 * - 75644:10081  — What Ifs right panel (title, slider, Reset/Save)
 *
 * Full-page rebuild reference (supersedes the frames above for layout/shell):
 * - 76685:4703  — "Planner page-chart+table", the current source of truth
 * - 76685:5207  — Header strip: title row + divider + toolbar row
 * - 76685:11650 — KPI row (uniform cards, no shared container)
 * - 76685:6566  — Chart card (toolbar strip + chart + legend)
 * - 76685:5630  — Table card (same toolbar strip + grid)
 */
import { accent, neutral, primaryAction, radius, text } from 'styles/revampTokens';

/**
 * Chrome tokens are re-exported from `styles/revampTokens` — the same file the
 * header revamp and DimensionsRevamp import from. The plan page previously
 * carried its own parallel set (#D9D9D9 borders, #708090 secondary text, 10px
 * radii, 40px controls) which drifted from the other revamped modules on every
 * one of those values. Anything that could come from a shared token now does.
 */
export {
  accent,
  neutral,
  primaryAction,
  text,
  controlHeight as PLAN_TABLE_TIME_TOGGLE_SIZE,
} from 'styles/revampTokens';
export { controlHeight, font, fontSize, shadow, space } from 'styles/revampTokens';

/** Cards, panels, the table shell. */
export const PLAN_REVAMP_RADIUS = radius.surface;
/** Buttons, inputs, chips — was 10px, which matched nothing else in the app. */
export const PLAN_REVAMP_RADIUS_SM = radius.control;
export const PLAN_REVAMP_RADIUS_XS = radius.control;

/**
 * Page background — the light gradient every other revamped module renders on
 * (TabContent gives Dashboard/Dimensions/Blocks the identical value).
 */
export const PLAN_PAGE_GRADIENT =
  'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)';
/**
 * The gradient's own first stop — for the KPI row's scroll edge fade, which
 * sits near the top of the page where this stop dominates. A `mask-image`
 * fade (revealing the true page background, whatever it is) was tried here
 * first and reverted: fading a bordered/tinted card to alpha-transparent
 * looks like a rendering glitch — a card visibly dissolving — rather than a
 * clean edge, especially right where the opaque scroll button sits over it.
 * A painted fade to an approximate match avoids that; `neutral.surface`
 * (pure white) was the wrong approximation, since the real background here
 * is this cream tint, not white.
 */
export const PLAN_PAGE_GRADIENT_TOP = '#FCFAF8';

/** Selected KPI column — an accent tint, because selection is an active state. */
/**
 * Selected KPI column (Figma 76657:4733).
 *
 * A top-down gradient, not the flat `accent.subtle` this briefly used: the tint
 * fades out before the card's bottom edge, which is what lets the selected
 * column read as joined to the surface below instead of as a solid block
 * stopping at a hard line. Figma gives it no border at all — the fade is the
 * whole effect, and an accent outline around it just boxed it back in.
 */
export const PLAN_KPI_SELECTED_BG = 'linear-gradient(180deg, #F5F0FF 0%, #FFFFFF 148.91%)';
/**
 * Kept for the data tables, which use it for their selected-column edge. The
 * KPI card itself no longer draws a border — see above.
 */
export const PLAN_KPI_SELECTED_BORDER = accent.border;
/** Figma's positive/variance green for the selected card's sub-label. */
export const PLAN_KPI_POSITIVE = '#22C55E';

/**
 * Every KPI card is exactly this wide — fixed, not a min/max range. Figma
 * 76685:11650 (the newer "Planner page-chart+table" frame): 160px cards,
 * 20px gap, no shared container — they float directly on the page gradient.
 *
 * Letting cards size to their content meant each one was a different width
 * depending on how long its indicator happened to be named, so the row never
 * settled into a rhythm and the cards nearest a long name looked stretched.
 * A single width also means the horizontal scroll steps land predictably.
 * Names longer than the card ellipsis; the full text is on the card's tooltip.
 */
export const PLAN_KPI_WIDTH = '160px';
/** Gap between KPI cards in the row. Was 20px (Figma 76685:11650); tightened per feedback that the row read as too spaced-out. */
export const PLAN_KPI_GAP = '12px';
/** Gap between the label, value and sub-label inside a column. */
export const PLAN_KPI_STACK_GAP = '8px';
/** Card interior padding, Figma 76685:5495 (KPI cards) and the chart/table card bodies. */
export const PLAN_CARD_PADDING = '16px';
/** "Edited …" metadata line only. */
export const PLAN_ACCENT_BLUE = '#3BA7FA';
export const PLAN_BUILDER_MODE_BG = primaryAction.bg;
export const PLAN_COMPARE_SCENARIO_DOT = '#008130';

/** Chrome aliases so plan-page call sites can stop hardcoding hexes. */
export const PLAN_SURFACE = neutral.surface;
export const PLAN_BORDER = neutral.border;
export const PLAN_BORDER_STRONG = neutral.borderStrong;
export const PLAN_SURFACE_HOVER = neutral.surfaceHover;
export const PLAN_TEXT_PRIMARY = text.primary;
export const PLAN_TEXT_SECONDARY = text.secondary;
export const PLAN_TEXT_MUTED = text.muted;

export const PLAN_SEGMENT_TRACK_BG = '#F7F7FB';
export const PLAN_SEGMENT_ACTIVE_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.08)';

export const PLAN_WHATIFS_PANEL_WIDTH = '283px';
export const PLAN_CHART_VIEW_HEIGHT = '460px';
/** Chart share of viewport in Chart + Table split view */
export const PLAN_CHART_SPLIT_VIEW_RATIO = '70%';
/** Minimum table share in Chart + Table split view */
export const PLAN_TABLE_SPLIT_VIEW_RATIO = '30%';
/** @deprecated Use ratio-based split layout in chart-table view */
export const PLAN_CHART_SPLIT_VIEW_HEIGHT = '300px';
export const PLAN_MAIN_CONTENT_GAP = '12px';
/**
 * Page inset — Chakra spacing 3 (12px), the same value DimensionsRevamp's page
 * shell uses (`<Flex px={3}>` in DimensionsRevamp/index.tsx).
 *
 * This was 18 (72px), taken literally off the Figma desktop artboard. At that
 * width the plan page's content started an inch further in than every other
 * revamped module's, which is exactly the kind of drift that makes separately
 * built screens read as separate products. The Figma inset describes one
 * artboard width; the app has to hold at any width, so the shared 12px wins.
 */
export const PLAN_HEADER_HORIZONTAL_PADDING = 3;
/**
 * Toolbar metrics.
 *
 * The row started at 48px with 32px controls floating inside it, which gave the
 * chrome more vertical weight than the KPI content above it. Dropping to 40px
 * fixed that but left the 28px controls with only 6px of air either side, so
 * they read as jammed against the edges. 44px is the settled value: the
 * controls keep their reduced size — these are all real sizes, never a
 * transform, so text stays crisp and hit targets stay honest — and the row gets
 * back enough breathing room to look deliberate.
 */
export const PLAN_TOOLBAR_HEIGHT = '44px';
export const PLAN_TOOLBAR_ARROW_TAB_HEIGHT = 28;
export const PLAN_TOOLBAR_ARROW_TAB_WIDTH = 88;
export const PLAN_FILTER_PILL_HEIGHT = '28px';
/** Icon size for the toolbar's controls — down from 16px with the row. */
export const PLAN_TOOLBAR_ICON_SIZE = 14;
/** Figma Red (alert) */
export const PLAN_FILTER_BADGE_BG = '#FE552F';

export const PLAN_TABLE_TOOLBAR_HEIGHT = '40px';
/**
 * M/Q/Y toggle squares — same size as every other control in the revamp
 * family, `controlHeight` (32px, via the `PLAN_TABLE_TIME_TOGGLE_SIZE`
 * re-export above), matching DimensionsRevamp exactly. Was 36px, off a
 * different Figma frame; Dimensions has no M/Q/Y control of its own and
 * runs everything at 32px.
 */
export const PLAN_TABLE_TIME_TOGGLE_RADIUS = radius.control;
/**
 * Table chrome, mirroring `DimensionsRevamp/DimensionsTableTheme.scss` so the
 * two grids read as the same component.
 *
 * That file's values are: grid `#D1D6DD`, header surface `#F5F0FF`, header text
 * `#5B6472`, cell text `#1F2430`, 13px cells / 12px headers, `7px 9px 6px 9px`
 * cell padding. The plan grid had drifted from every one of them — its gridlines
 * were `accent.border` (#C4B5FD), i.e. purple, its header carried a gradient
 * rather than a flat tint, and its cell borders were a third grey (#E8E9EB).
 */
export const PLAN_TABLE_CELL_BORDER = neutral.border;
/**
 * The table card's toolbar strip — the row carrying the hint text, search,
 * M/Q/Y and Download. Stays white/`neutral.surface`, matching the card around
 * it and the toolbar above the KPI row; the grid's own period-header row below
 * it is the one that carries the Dimensions tint, not this.
 */
export const PLAN_TABLE_TOOLBAR_BG = neutral.surface;
/**
 * Period-header row and sticky metric column, inside the grid itself — light
 * purple `accent.subtle` (#F5F0FF), matching both the Dimensions grid and the
 * Figma frame for this table.
 */
export const PLAN_TABLE_HEADER_BG = accent.subtle;
/** Sticky metric column — same tint as the period header, per Dimensions/Figma. */
export const PLAN_TABLE_STICKY_BG = accent.subtle;
export const PLAN_TABLE_HEADER_TEXT = text.secondary;
/** Cell padding, from the Dimensions grid's `7px 9px 6px 9px`. */
export const PLAN_TABLE_CELL_PY = '7px';
export const PLAN_TABLE_CELL_PX = '9px';
export const PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH = '211px';

/**
 * Chart/table card corner radius — `radius.surface` (12px), matching every
 * other card in the revamp family. Was 16px, off the older Figma frame; the
 * newer "Planner page-chart+table" frame (76685:6566 / 76685:5630) uses 12px
 * like everything else.
 */
export const PLAN_CHART_CARD_RADIUS = radius.surface;
/**
 * The card-toolbar strip shared by the chart card and the table card —
 * Figma 76685:6567 / 76685:5631: hint text left, M/Q/Y + Download right.
 * One component (`PlanCardToolbarStrip`) renders both.
 */
/** Content-derived, not fixed — `py='8px'` around a 32px `controlHeight` row, same as Dimensions' toolbar (it has no fixed height either). */
export const PLAN_CARD_TOOLBAR_PY = '8px';
/** The chart/grid's own inset bordered box inside the card body. */
export const PLAN_CARD_INNER_RADIUS = radius.control;
/** Figma chart series palette (75644:7652) */
export const PLAN_CHART_SERIES_COLORS = [
  '#8B5CF6',
  '#11CDDA',
  '#22C55E',
  '#FE552F',
  '#3BA7FA',
] as const;

export { PLAN_FONT, PLAN_TEXT } from './planPageRevampTypography';
export type { PlanTextVariant } from './planPageRevampTypography';
